import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useMyList } from '../../hooks/useMyList';
import {
  PILL_MIN_HEIGHT, PILL_RADIUS, pillBg, pillBgHover,
} from './headerPill';

interface MyListButtonProps {
  /** When true, render as a full-width list row (inside the mobile menu). */
  forceLabel?: boolean;
  /** Render as a compact header icon matching the mobile menu button. */
  iconOnly?: boolean;
  /** Fired on click (e.g. to close the mobile menu before navigating). */
  onNavigate?: () => void;
}

/** Header entry point to the My List page, with an inline saved-school count. */
const MyListButton = ({
  forceLabel,
  iconOnly,
  onNavigate,
}: MyListButtonProps = {}) => {
  const { t } = useTranslation();
  const items = useMyList();
  const count = items.length;
  const hasFavorites = count > 0;
  const [lastPositiveCount, setLastPositiveCount] = useState(count);
  const ariaLabel = hasFavorites
    ? `${t('finder.mylist_title')} (${count})`
    : t('finder.mylist_title');

  useEffect(() => {
    if (hasFavorites) {
      setLastPositiveCount(count);
      return undefined;
    }

    const timer = window.setTimeout(() => setLastPositiveCount(0), 260);
    return () => window.clearTimeout(timer);
  }, [count, hasFavorites]);

  const displayCount = hasFavorites ? count : lastPositiveCount;

  const label = (
    <>
      {t('finder.mylist_title')}
      <Box
        component="span"
        aria-hidden={!hasFavorites}
        sx={{
          display: 'inline-block',
          maxWidth: hasFavorites ? '5ch' : 0,
          ml: hasFavorites ? '6px' : 0,
          opacity: hasFavorites ? 1 : 0,
          transform: hasFavorites ? 'translateX(0)' : 'translateX(-4px)',
          overflow: 'hidden',
          color: 'text.secondary',
          fontWeight: 500,
          verticalAlign: 'bottom',
          whiteSpace: 'nowrap',
          transition: hasFavorites
            ? 'max-width 180ms ease, margin-left 180ms ease, opacity 120ms ease 80ms, transform 120ms ease 80ms'
            : 'opacity 100ms ease, transform 100ms ease, max-width 180ms ease 80ms, margin-left 180ms ease 80ms',
        }}
      >
        (
        {displayCount}
        )
      </Box>
    </>
  );

  const icon = hasFavorites
    ? <FavoriteIcon sx={{ color: 'error.main', fontSize: forceLabel ? 20 : 18 }} />
    : <FavoriteBorderIcon sx={{ color: 'text.secondary', fontSize: forceLabel ? 20 : 18 }} />;

  if (iconOnly) {
    return (
      <IconButton
        component={RouterLink}
        to="/my-list"
        onClick={onNavigate}
        aria-label={ariaLabel}
        sx={{
          minWidth: PILL_MIN_HEIGHT,
          height: PILL_MIN_HEIGHT,
          p: 1.25,
          borderRadius: PILL_RADIUS,
          bgcolor: pillBg,
          color: hasFavorites ? 'error.main' : 'text.secondary',
          overflow: 'hidden',
          '&:hover': { bgcolor: pillBgHover },
        }}
      >
        {hasFavorites
          ? <FavoriteIcon sx={{ flexShrink: 0, fontSize: 20 }} />
          : <FavoriteBorderIcon sx={{ flexShrink: 0, fontSize: 20 }} />}
        <Box
          component="span"
          aria-hidden={!hasFavorites}
          sx={{
            maxWidth: hasFavorites ? '3ch' : 0,
            ml: hasFavorites ? '6px' : 0,
            opacity: hasFavorites ? 1 : 0,
            transform: hasFavorites ? 'translateX(0)' : 'translateX(-4px)',
            overflow: 'hidden',
            fontSize: '0.75rem',
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            transition: hasFavorites
              ? 'max-width 180ms ease, margin-left 180ms ease, opacity 120ms ease 80ms, transform 120ms ease 80ms'
              : 'opacity 100ms ease, transform 100ms ease, max-width 180ms ease 80ms, margin-left 180ms ease 80ms',
          }}
        >
          {displayCount}
        </Box>
      </IconButton>
    );
  }

  if (forceLabel) {
    return (
      <Button
        component={RouterLink}
        to="/my-list"
        onClick={onNavigate}
        aria-label={t('finder.mylist_title')}
        startIcon={icon}
        sx={{
          justifyContent: 'flex-start',
          borderRadius: 0,
          px: 2,
          minHeight: 52,
          color: 'text.primary',
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'none',
          '& .MuiButton-startIcon': { ml: 0, mr: 1.5 },
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      component={RouterLink}
      to="/my-list"
      onClick={onNavigate}
      aria-label={t('finder.mylist_title')}
      startIcon={icon}
      sx={{
        minHeight: PILL_MIN_HEIGHT,
        borderRadius: PILL_RADIUS,
        px: 2,
        color: 'text.primary',
        bgcolor: pillBg,
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { bgcolor: pillBgHover },
        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
      }}
    >
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        {label}
      </Box>
    </Button>
  );
};

export default MyListButton;
